import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, LoaderCircle, Minus, Plus } from 'lucide-react';
import { formatCurrency, onlyNumbers } from './formularioParts/utils';
import { CriancaEvento, ItemEvento, criarCompraEvento, fetchCriancasEvento, fetchItensEvento } from './eventoAdicionalData';

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const formatCpf = (value: string) =>
  onlyNumbers(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

type Etapa = 'nome' | 'crianca' | 'itens';

const EventoAdicionalPublico: React.FC = () => {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState<Etapa>('nome');
  const [nomeResponsavel, setNomeResponsavel] = useState('');
  const [cpfResponsavel, setCpfResponsavel] = useState('');

  const [criancas, setCriancas] = useState<CriancaEvento[]>([]);
  const [buscaCrianca, setBuscaCrianca] = useState('');
  const [criancaSelecionada, setCriancaSelecionada] = useState<CriancaEvento | null>(null);

  const [itensDisponiveis, setItensDisponiveis] = useState<ItemEvento[]>([]);
  const [quantidades, setQuantidades] = useState<Record<string, number>>({});

  const [carregandoDados, setCarregandoDados] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState('');
  const [gerandoPagamento, setGerandoPagamento] = useState(false);
  const [erroPagamento, setErroPagamento] = useState('');

  useEffect(() => {
    let ativo = true;
    Promise.all([fetchCriancasEvento(), fetchItensEvento()])
      .then(([listaCriancas, listaItens]) => {
        if (!ativo) return;
        setCriancas(listaCriancas);
        setItensDisponiveis(listaItens);
      })
      .catch((error: Error) => {
        if (ativo) setErroCarregamento(error.message);
      })
      .finally(() => {
        if (ativo) setCarregandoDados(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const sugestoes = useMemo(() => {
    const busca = normalizeText(buscaCrianca.trim());
    if (busca.length < 2) return [];
    return criancas.filter((crianca) => normalizeText(crianca.nomeCompleto).includes(busca)).slice(0, 8);
  }, [buscaCrianca, criancas]);

  const cpfNumeros = onlyNumbers(cpfResponsavel);
  const descontoSecreto = cpfNumeros === '88888888888' ? 'bingo' : cpfNumeros === '99999999999' ? 'cachorro-quente' : cpfNumeros === '77777777777' ? 'todos' : '';
  const itemGratuito = (item: ItemEvento) => descontoSecreto === 'todos' || item.id === descontoSecreto;
  const valorVisualItem = (item: ItemEvento) => (itemGratuito(item) ? 0 : item.valor);
  const valorSelecionado = itensDisponiveis.reduce(
    (soma, item) => soma + valorVisualItem(item) * (quantidades[item.id] || 0),
    0
  );
  const itensEscolhidos = Object.entries(quantidades).filter(([, quantidade]) => quantidade > 0);

  const nomeValido = nomeResponsavel.trim().length > 2;
  const cpfValido = onlyNumbers(cpfResponsavel).length === 11;

  const selecionarCrianca = (crianca: CriancaEvento) => {
    setCriancaSelecionada(crianca);
    setBuscaCrianca('');
  };

  const alternarItem = (id: string) => {
    setQuantidades((atual) => {
      const proximo = { ...atual };
      if (proximo[id] > 0) delete proximo[id];
      else proximo[id] = 1;
      return proximo;
    });
  };

  const ajustarQuantidade = (id: string, delta: number) => {
    setQuantidades((atual) => {
      const atual2 = Math.max(0, (atual[id] || 0) + delta);
      const proximo = { ...atual };
      if (atual2 <= 0) delete proximo[id];
      else proximo[id] = atual2;
      return proximo;
    });
  };

  const gerarPagamento = async () => {
    if (!criancaSelecionada || !itensEscolhidos.length) return;
    setGerandoPagamento(true);
    setErroPagamento('');
    try {
      const resultado = await criarCompraEvento({
        nomeResponsavel: nomeResponsavel.trim(),
        cpfResponsavel: cpfNumeros,
        inscricaoId: criancaSelecionada.id,
        itens: itensEscolhidos.map(([id, quantidade]) => ({ id, quantidade }))
      });
      // Rota própria: um reload na tela de pagamento não perde os dados, o link recarrega tudo pelo id.
      navigate(`/evento/pagamento/${resultado.compraId}`);
    } catch (error) {
      setErroPagamento((error as Error).message);
    } finally {
      setGerandoPagamento(false);
    }
  };

  const etapas: Etapa[] = ['nome', 'crianca', 'itens'];
  const passoAtual = etapas.indexOf(etapa) + 1;

  return (
    <div className="checkout-overlay evento-page">
      <div className="evento-page-content">
        <div className="evento-logo-wrap">
          <img src="/logo.png" alt="Colônia de Férias" className="evento-logo" />
        </div>
        <div className="checkout-modal">
          <div className="checkout-header">
            <div>
              <h2>Inscrição no Evento</h2>
              <p>Bingo e Cachorro-quente</p>
            </div>
          </div>

        <div className="evento-steps">
          {etapas.map((item, index) => (
            <span key={item} className={index + 1 <= passoAtual ? 'evento-step active' : 'evento-step'} />
          ))}
        </div>

        {carregandoDados && (
          <div className="checkout-result">
            <LoaderCircle className="spin" size={32} />
            <p>Carregando...</p>
          </div>
        )}

        {!carregandoDados && erroCarregamento && (
          <div className="checkout-form">
            <div className="checkout-error">{erroCarregamento}</div>
          </div>
        )}

        {!carregandoDados && !erroCarregamento && etapa === 'nome' && (
          <form
            className="checkout-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (nomeValido && cpfValido) setEtapa('crianca');
            }}
          >
            <div className="checkout-fields">
              <label>
                Nome completo do responsável
                <input value={nomeResponsavel} onChange={(event) => setNomeResponsavel(event.target.value)} autoFocus />
              </label>
              <label>
                CPF
                <input
                  inputMode="numeric"
                  value={cpfResponsavel}
                  onChange={(event) => setCpfResponsavel(formatCpf(event.target.value))}
                  placeholder="000.000.000-00"
                />
              </label>
            </div>
            <button type="submit" className="checkout-primary" disabled={!nomeValido || !cpfValido}>
              Continuar
            </button>
          </form>
        )}

        {!carregandoDados && !erroCarregamento && etapa === 'crianca' && (
          <div className="checkout-form">
            <button type="button" className="evento-voltar" onClick={() => setEtapa('nome')}>
              <ChevronLeft size={16} /> Voltar
            </button>
            <label>
              Nome da criança
              <input
                value={criancaSelecionada ? criancaSelecionada.nomeCompleto : buscaCrianca}
                onChange={(event) => {
                  setCriancaSelecionada(null);
                  setBuscaCrianca(event.target.value);
                }}
                placeholder="Digite o nome da criança"
                autoFocus
              />
            </label>
            {!criancaSelecionada && sugestoes.length > 0 && (
              <ul className="evento-suggestions">
                {sugestoes.map((crianca) => (
                  <li key={crianca.id}>
                    <button type="button" onClick={() => selecionarCrianca(crianca)}>
                      {crianca.nomeCompleto}
                      {crianca.turma ? <span> - Turma {crianca.turma}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!criancaSelecionada && buscaCrianca.trim().length >= 2 && sugestoes.length === 0 && (
              <p className="report-muted">Nenhuma criança encontrada com esse nome.</p>
            )}
            <button
              type="button"
              className="checkout-primary"
              disabled={!criancaSelecionada}
              onClick={() => setEtapa('itens')}
            >
              Continuar
            </button>
          </div>
        )}

        {!carregandoDados && !erroCarregamento && etapa === 'itens' && (
          <div className="checkout-form">
            <button type="button" className="evento-voltar" onClick={() => setEtapa('crianca')}>
              <ChevronLeft size={16} /> Voltar
            </button>
            <p className="evento-crianca-selecionada">
              Criança: <strong>{criancaSelecionada?.nomeCompleto}</strong>
            </p>
            <div className="evento-itens-lista">
              {itensDisponiveis.map((item) => {
                const quantidade = quantidades[item.id] || 0;
                return (
                  <div key={item.id} className="evento-item-row">
                    <label className="evento-item-checkbox">
                      <input type="checkbox" checked={quantidade > 0} onChange={() => alternarItem(item.id)} />
                      <span>{item.nome}</span>
                    </label>
                    {item.permiteQuantidade && quantidade > 0 && (
                      <div className="evento-item-stepper">
                        <button type="button" onClick={() => ajustarQuantidade(item.id, -1)}>
                          <Minus size={14} />
                        </button>
                        <span>{quantidade}</span>
                        <button type="button" onClick={() => ajustarQuantidade(item.id, 1)}>
                          <Plus size={14} />
                        </button>
                      </div>
                    )}
                    <strong>{formatCurrency(valorVisualItem(item) * (quantidade || 1))}</strong>
                  </div>
                );
              })}
            </div>
            <div className="checkout-summary">
              <span>Total</span>
              <strong>{formatCurrency(valorSelecionado)}</strong>
            </div>
            {erroPagamento && <div className="checkout-error">{erroPagamento}</div>}
            <button
              type="button"
              className="checkout-primary"
              disabled={!itensEscolhidos.length || gerandoPagamento}
              onClick={gerarPagamento}
            >
              {gerandoPagamento && <LoaderCircle className="spin" size={18} />}
              {valorSelecionado === 0 ? 'Finalizar gratuito' : 'Ir para pagamento'}
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default EventoAdicionalPublico;
